import { useEffect, useState } from "react"
import type { FC } from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router"

import { PasswordTextField } from "@/components/form/password-text-field"
import { useAuthentication } from "@/contexts"
import type { SignInFailure } from "@/contexts/authentication-context/authentication-context-utils"
import { RootPath } from "@/models/route"
import { Form, FormTextField, SubmitButton, useForm } from "@/nzds/forms"
import { useGtag } from "@/services/gtag"
import { useSSOForInternalUsers } from "@/services/sso"
import { resolveSafeSignInReturnPath } from "@/utils/auth/auth"
import { APP_COMPANY_NAME } from "@/utils/constants"

import {
  Box,
  Button,
  Collapse,
  Divider,
  Fade,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"

import { WhiteBox } from "../whiteBox"
import { loginSchema } from "./login.schema"
import type { LoginSchema } from "./login.schema"
import { SignInFailureAlert } from "./sign-in-failure-alert"

export const Login: FC = () => {
  const { t } = useTranslation()
  const { gtagData } = useGtag()
  const theme = useTheme()
  const { activeUser, isAuthenticated, isAuthenticating, signIn } =
    useAuthentication()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [signInFailure, setSignInFailure] = useState<SignInFailure | null>(null)

  const defaultFormValues: LoginSchema = {
    email: "",
    password: "",
  }

  const form = useForm({
    defaultValues: defaultFormValues,
    mode: "onTouched",
    schema: loginSchema,
  })

  const email = form.watch("email")

  const { isSSORequiredForInternalUsers } = useSSOForInternalUsers({
    email,
  })

  const handlePasswordVisibleClick = (): void => {
    setIsPasswordVisible((isVisible) => !isVisible)
  }

  const handleSubmit: SubmitHandler<LoginSchema> = async (
    value
  ): Promise<void> => {
    if (isSSORequiredForInternalUsers) {
      return
    }

    setSignInFailure(null)

    const result = await signIn(value)

    if (result === "success") {
      // Must set the user_id before sending the login event
      gtagData?.gtag("set", {
        user_id: value.email,
      })

      gtagData?.gtag("event", "login", {
        method: "nzero",
      })
      return
    }

    setSignInFailure(result)
  }

  const redirectPath = searchParams.get("from")

  useEffect(() => {
    const subscription = form.watch((_values, info) => {
      if (info.name === "email" || info.name === "password") {
        setSignInFailure(null)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [form])

  useEffect(() => {
    if (isAuthenticated && activeUser) {
      const landingPath = resolveSafeSignInReturnPath(redirectPath)

      void navigate(landingPath)
    }
  }, [activeUser, isAuthenticated, navigate, redirectPath])

  return (
    <Fade in={true}>
      <div>
        <WhiteBox aria-labelledby="logInTitle">
          <Typography
            id="logInTitle"
            variant="h1"
            sx={{
              mb: 4,
              textAlign: "center",
            }}
          >
            {t("login.title")}
          </Typography>

          <Collapse in={signInFailure !== null}>
            {signInFailure !== null && (
              <Box
                sx={{
                  pb: 2,
                }}
              >
                <SignInFailureAlert failure={signInFailure} />
              </Box>
            )}
          </Collapse>
          <Form<LoginSchema>
            aria-labelledby="logInTitle"
            form={form}
            onSubmit={handleSubmit}
          >
            <Box
              sx={{
                mb: 1.5,
              }}
            >
              <FormTextField<LoginSchema>
                autoComplete="username"
                fullWidth
                error={isSSORequiredForInternalUsers}
                helperText={
                  isSSORequiredForInternalUsers
                    ? t("login.mustUseSSOError")
                    : undefined
                }
                id="email"
                label={t("fields.email.title")}
                name="email"
                preserveHelperTextSpacing
                variant="stacked"
              />
              <PasswordTextField<LoginSchema>
                autoComplete="current-password"
                id="password"
                isPasswordVisible={isPasswordVisible}
                label={t("fields.password.title")}
                onPasswordVisibleClick={handlePasswordVisibleClick}
                variant="stacked"
              />
              <Typography
                variant="caption"
                sx={{
                  ml: 0.5,
                }}
              >
                <Link
                  component={RouterLink}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                  to={`/${RootPath.ForgotPassword}`}
                >
                  {t("login.forgotPasswordLink")}
                </Link>
              </Typography>
            </Box>

            <SubmitButton
              disabled={isSSORequiredForInternalUsers}
              fullWidth
              loading={isAuthenticating || form.formState.isSubmitting}
            >
              {t("login.logInWithCompanyButton", {
                companyName: APP_COMPANY_NAME,
              })}
            </SubmitButton>
          </Form>

          <Typography
            variant="body2"
            sx={{
              mb: 3.5,
              mt: 3.5,
              color: theme.palette.text.secondary,
            }}
          >
            <Divider component="span">{t("login.or")}</Divider>
          </Typography>
          <Stack
            sx={{
              alignItems: "center",
            }}
          >
            <Button color="primary" href="/sso_login" variant="outlined">
              {t("login.logInWithSSOButton")}
            </Button>
          </Stack>
        </WhiteBox>
      </div>
    </Fade>
  )
}
